/**
 * Minimal client error reporter.
 *
 * The app ships no third-party error service, and the API has no
 * error-ingest endpoint, so a production crash previously left no trace at
 * all: ErrorBoundary logged to the console and nothing else. This module is
 * the smallest thing that makes a crash recoverable after the fact —
 *
 *   - console.error, always (vite.config.js deliberately keeps console.error
 *     and console.warn in release builds for exactly this reason)
 *   - a bounded ring buffer in localStorage, so support/QA can read what
 *     happened out of the affected browser after the fact
 *   - window.__vowseraErrors, for reading the buffer from a devtools console
 *     without knowing the storage key
 *   - setErrorSink(), a single seam to forward everything to Sentry or a
 *     backend endpoint once one exists
 *
 * It is NOT a substitute for a real error service. It cannot tell you about
 * crashes you were not told about.
 */

const STORAGE_KEY = 'vowsera:error-log';
const MAX_ENTRIES = 25;
const MAX_STACK_CHARS = 4000;

/** Optional forwarder, set by the app once a real reporting service exists. */
let sink = null;

/**
 * Register a forwarder for every reported error.
 * @param {(entry: object) => void} fn
 */
export function setErrorSink(fn) {
  sink = typeof fn === 'function' ? fn : null;
}

function readLog() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLog(entries) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
  } catch {
    /* quota or private mode — the console.error still happened */
  }
}

/** Everything support needs, and nothing that identifies the user. */
function buildEntry(error, context) {
  const err = error || {};
  return {
    at: new Date().toISOString(),
    message: String(err.message || err.toString?.() || err || 'Unknown error'),
    name: err.name || 'Error',
    stack: err.stack ? String(err.stack).slice(0, MAX_STACK_CHARS) : null,
    path: typeof window !== 'undefined' ? window.location.pathname + window.location.search : null,
    context: context || {}
  };
}

/**
 * Record an error. Safe to call from anywhere, including inside a catch —
 * it never throws.
 */
export function reportError(error, context = {}) {
  let entry;
  try {
    entry = buildEntry(error, context);
  } catch {
    return null;
  }

  // Kept in release builds on purpose — see vite.config.js.
  console.error('[vowsera]', entry.message, error, context);

  try {
    writeLog([...readLog(), entry]);
  } catch {
    /* non-fatal */
  }

  if (sink) {
    try {
      sink(entry);
    } catch {
      /* a broken sink must never take down the page */
    }
  }

  return entry;
}

/** Read the stored errors (newest last). */
export function getErrorLog() {
  return readLog();
}

/** Drop the stored errors. */
export function clearErrorLog() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Catch what React's ErrorBoundary cannot: errors thrown outside the render
 * tree, and promise rejections nobody handled. Idempotent.
 */
export function installGlobalErrorHandlers() {
  if (typeof window === 'undefined' || window.__vowseraErrorHandlersInstalled) return;
  window.__vowseraErrorHandlersInstalled = true;

  window.addEventListener('error', (event) => {
    // Resource load failures (img/script 404s) surface here with no `error`
    // object and would otherwise flood the buffer.
    if (!event.error) return;
    reportError(event.error, { source: 'window.onerror' });
  });

  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { source: 'unhandledrejection' });
  });

  // Convenience accessor so a support call can be "open devtools, paste this".
  window.__vowseraErrors = getErrorLog;
}

export default {
  reportError,
  getErrorLog,
  clearErrorLog,
  setErrorSink,
  installGlobalErrorHandlers
};
